import { useEffect, useState } from "react";
import {
  signInWithRedirect,
  signOut,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import "@aws-amplify/ui-react/styles.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ApiCatalog } from "./pages/ApiCatalog";
import { ApiDetail } from "./pages/ApiDetail";
import { ApiKeys } from "./pages/ApiKeys";
import { Dashboard } from "./pages/Dashboard";
import { getConfig } from "./config";

interface AuthUser {
  username: string;
  userId: string;
  signInDetails?: {
    loginId?: string;
  };
  displayName?: string;
  email?: string;
  groups?: string[];
}

function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();

    // Listen for auth events (e.g., redirect back from IdP)
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          checkUser();
          break;
        case "signInWithRedirect_failure":
          console.error("Federated sign-in failed:", payload.data);
          setLoading(false);
          break;
        case "signedOut":
          setUser(null);
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      // Extract name/email from the ID token claims (no extra scopes needed)
      let displayName: string | undefined;
      let email: string | undefined;
      let groups: string[] = [];
      try {
        const session = await fetchAuthSession();
        const claims = session.tokens?.idToken?.payload;
        if (claims) {
          displayName =
            (claims.name as string) ??
            (claims.preferred_username as string);
          email = claims.email as string;
          // Cognito puts groups in "cognito:groups" claim
          const rawGroups = claims["cognito:groups"];
          if (Array.isArray(rawGroups)) {
            groups = rawGroups as string[];
          }
        }
      } catch {
        // Session claims may not be available immediately after redirect
      }
      setUser({
        ...currentUser,
        displayName,
        email,
        groups,
      });
    } catch {
      // Not signed in
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    setUser(null);
  }

  async function handleFederatedSignIn() {
    const { idpProviderName } = getConfig();
    await signInWithRedirect({
      provider: { custom: idpProviderName },
    });
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <p>Loading...</p>
        <style>{`
          .loading-screen { display: flex; align-items: center; justify-content: center; min-height: 100vh; font-size: 1.1rem; color: var(--color-text-muted); }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>M&B Internal API Developer Portal</h1>
          <p>Sign in with your organization account to access the portal.</p>
          <button onClick={handleFederatedSignIn} className="federated-login-btn">
            Sign in with SSO
          </button>
        </div>
        <style>{`
          .login-screen { display: flex; align-items: center; justify-content: center; min-height: 100vh; background: var(--color-bg); }
          .login-card { text-align: center; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; padding: 3rem; max-width: 400px; width: 100%; }
          .login-card h1 { margin-bottom: 0.75rem; font-size: 1.5rem; }
          .login-card p { color: var(--color-text-muted); margin-bottom: 2rem; }
          .federated-login-btn { background: var(--color-primary); color: white; border: none; padding: 0.75rem 2rem; border-radius: 6px; font-size: 1rem; font-weight: 500; cursor: pointer; width: 100%; }
          .federated-login-btn:hover { opacity: 0.9; }
        `}</style>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Layout user={user} onSignOut={handleSignOut}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/apis" element={<ApiCatalog userGroups={user.groups ?? []} />} />
          <Route path="/apis/:apiName" element={<ApiDetail userGroups={user.groups ?? []} />} />
          <Route path="/keys" element={<ApiKeys />} />
          <Route path="/callback" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
